import { isToolPart, toolNameOf, WIKI_REPLY_TOOL, type WikiChangeSet } from "@esi/shared";
import { Bot, Send } from "lucide-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { InlineError } from "@/components/Notice";
import { StepProgress } from "@/components/StepProgress";
import { Button } from "@/components/ui/button";
import { ApiError } from "../api-errors.ts";
import { useOpenWiki } from "../router.tsx";
import { useChangeSet } from "./queries.ts";
import { countsText, useResolutionRun, useResolutionWrite } from "./resolve.ts";

// The author's side of Resolve with the agent (wiki-resolve-with-agent D7): the trigger on their
// change card and preview, the turn's sent line, and the run card with its progress, then its
// settled state. Suggestion anatomy (DESIGN.md "Suggestion cards"): dashed, quiet, no toast.

/**
 * "Resolve with the agent (N)" (D1): only when the set's read for its creator carries an
 * eligible `resolve` field (any other viewer, a set made elsewhere or an N-1 API: nothing), and
 * not while a run of the set is in progress (its card shows the progress).
 */
export function ResolveTrigger(props: {
  set: Pick<WikiChangeSet, "resolve">;
  busy: boolean;
  onResolve: () => void;
  /** The hint under the button (the card's), or none (the preview's header). */
  hint?: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  const resolve = props.set.resolve;
  if (!resolve?.eligible || resolve.count === 0 || resolve.running) {
    return null;
  }
  const partial = resolve.of > resolve.count;
  return (
    <div className="flex flex-col gap-1.5">
      <div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={props.busy}
          aria-label={
            partial
              ? t("wiki.resolve.triggerLabelOf", { count: resolve.count, of: resolve.of })
              : t("wiki.resolve.triggerLabel", { count: resolve.count })
          }
          aria-describedby={props.hint ? `${id}-hint` : undefined}
          onClick={props.onResolve}
        >
          <Bot aria-hidden="true" strokeWidth={1.75} />
          {t("wiki.resolve.trigger")}
          {/* Keeps the label and the count two words for the name check (WCAG 2.5.3); the flex
              layout does not render it. */}{" "}
          <span
            aria-hidden="true"
            className="inline-flex h-4.5 min-w-5 items-center justify-center rounded-full bg-surface-selected px-1.5 text-[11px] font-semibold text-fg tabular-nums"
          >
            {partial
              ? t("wiki.resolve.triggerOf", { count: resolve.count, of: resolve.of })
              : resolve.count}
          </span>
        </Button>
      </div>
      {props.hint ? (
        <p id={`${id}-hint`} className="text-xs text-fg-muted">
          {t("wiki.resolve.triggerHint")}
        </p>
      ) : null}
    </div>
  );
}

/** A run's user message, settled into one quiet line (never the composed text, D2). */
export function ResolutionSentLine(props: {
  projectId: string;
  changeSetId: string;
  threadCount: number;
  title?: string;
}) {
  const { t } = useTranslation();
  const set = useChangeSet(props.projectId, props.changeSetId, !props.title);
  const title = props.title || (set.data && !set.data.hiddenItems ? set.data.title : "");
  return (
    <p className="flex items-center justify-end gap-1.5 self-end text-xs text-fg-muted">
      <Bot aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
      <span data-esi-content="wiki">
        {title
          ? t("wiki.resolve.sent", { count: props.threadCount, title })
          : t("wiki.resolve.sentUntitled", { count: props.threadCount })}
      </span>
    </p>
  );
}

/** How far the turn got, from its streamed tool parts: pages written, replies drafted. */
export function turnProgress(parts: readonly unknown[]) {
  let writes = 0;
  let writing = false;
  let replies = 0;
  const pages = new Set<string>();
  for (const part of parts) {
    if (!isToolPart(part)) {
      continue;
    }
    const p = part as { state?: unknown; input?: unknown; output?: unknown };
    const name = toolNameOf(part);
    const failed =
      p.state === "output-error" ||
      (p.state === "output-available" &&
        typeof (p.output as { error?: unknown } | undefined)?.error === "string");
    if (name === "wiki_write" && !failed) {
      writing = true;
      if (p.state === "output-available") {
        writes++;
        const operations = (p.input as { operations?: unknown } | undefined)?.operations;
        for (const op of Array.isArray(operations) ? operations : []) {
          const name =
            (op as Record<string, unknown>)?.title ?? (op as Record<string, unknown>)?.page;
          if (typeof name === "string") {
            pages.add(name);
          }
        }
      }
    } else if (name === WIKI_REPLY_TOOL && !failed && p.state === "output-available") {
      replies++;
    }
  }
  return { writes, writing, replies, pages: pages.size };
}

/**
 * The run card (D7) under the run's turn: the step progress while the turn streams (Reading N
 * comments → Revising pages → Drafting replies → Ready to review) in a polite live region, then
 * "Replies ready" with Review replies and Send all, a quiet line once sent or undone, or the
 * failure. The run's read is its creator's only: anyone else gets nothing beyond the sent line.
 */
export function ResolutionRunCard(props: {
  projectId: string;
  changeSetId: string;
  /** Null until the stored message (with the server's run) replaces the optimistic one. */
  runId: string | null;
  revision: number | null;
  threadCount: number;
  /** The turn's answer parts so far (its tool calls). */
  parts: readonly unknown[];
  /** This turn is the one in flight. */
  streaming: boolean;
  /** The conversation is the viewer's own and writable. */
  canAct: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  const { show } = useOpenWiki();
  const set = useChangeSet(props.projectId, props.changeSetId);
  const run = useResolutionRun(props.projectId, props.runId, !props.streaming);
  const write = useResolutionWrite(props.projectId, props.runId ?? "", props.changeSetId);
  if (run.error instanceof ApiError && run.error.status === 404) {
    return null;
  }
  const title = set.data && !set.data.hiddenItems ? set.data.title : "";
  const revision = run.data?.revision ?? props.revision;
  const heading = [
    revision ? t("wiki.resolve.revision", { revision }) : t("wiki.resolve.revisionPending"),
    ...(title ? [title] : []),
  ].join(" · ");
  const status = run.data?.status ?? null;
  const progress = turnProgress(props.parts);
  const steps = [
    { id: "read", label: t("wiki.resolve.step.read", { count: props.threadCount }) },
    { id: "revise", label: t("wiki.resolve.step.revise") },
    { id: "draft", label: t("wiki.resolve.step.draft") },
    { id: "ready", label: t("wiki.resolve.step.ready") },
  ];
  // Optimistic message, its turn over and no answer streamed: the start was refused.
  if (props.runId === null && !props.streaming && props.parts.length === 0) {
    return null;
  }
  const inFlight = props.streaming || status === "running" || (status === null && !run.isError);
  if (status === "sent" || status === "undone") {
    const counts = run.data ? countsText(run.data.counts) : "";
    return (
      <p
        role="status"
        className="flex items-start gap-2 text-xs text-fg-muted"
        data-resolution-run={status}
      >
        <Bot aria-hidden="true" className="mt-px size-3.5 shrink-0" strokeWidth={1.75} />
        <span data-esi-content="wiki" className="min-w-0 break-words">
          {heading} ·{" "}
          {status === "sent"
            ? `${t("wiki.resolve.settledSent")} · ${counts}`
            : t("wiki.resolve.settledUndone")}
        </span>
      </p>
    );
  }
  const current = !inFlight
    ? steps.length
    : props.streaming
      ? progress.replies > 0
        ? 2
        : progress.writing
          ? 1
          : 0
      : 3;
  const drafts = (run.data?.replies ?? []).filter((r) => r.status === "draft").length;
  const unanswered = run.data?.unanswered.length ?? 0;
  const pagesRevised = (run.data?.items ?? []).filter((i) => !i.undone).length;
  return (
    <section
      aria-labelledby={`${id}-title`}
      data-resolution-run={status ?? "running"}
      className="flex flex-col gap-2 rounded-lg border border-dashed border-line-strong px-3.5 py-3"
    >
      <h3
        id={`${id}-title`}
        className="flex items-center gap-2 text-[13px] font-medium text-fg-secondary"
      >
        <Bot aria-hidden="true" className="size-4 shrink-0 text-fg-muted" strokeWidth={1.75} />
        <span data-esi-content="wiki" className="min-w-0 break-words">
          {heading}
        </span>
      </h3>
      <div className="flex flex-col gap-2 pl-6">
        {status === "failed" ? (
          <>
            <StepProgress
              label={t("wiki.resolve.progress")}
              steps={steps}
              current={Math.min(current, steps.length - 1)}
              outcome="failed"
            />
            <p className="text-sm text-fg-secondary">{t("wiki.resolve.failed")}</p>
            {props.canAct &&
            run.data &&
            (run.data.replies.some((r) => r.status === "draft") ||
              run.data.items.some((i) => i.undoable && !i.undone)) ? (
              // What the turn wrote before it stopped stays reviewable: send or undo it.
              <div className="pt-1">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    show({
                      kind: "changes",
                      changeSetId: props.changeSetId,
                      itemId: null,
                      runId: props.runId,
                    })
                  }
                >
                  {t("wiki.resolve.review")}
                </Button>
              </div>
            ) : null}
          </>
        ) : inFlight ? (
          <>
            <StepProgress label={t("wiki.resolve.progress")} steps={steps} current={current} />
            {progress.pages || progress.replies ? (
              <p className="text-xs text-fg-secondary tabular-nums">
                {[
                  ...(progress.pages
                    ? [t("wiki.resolve.live.pages", { count: progress.pages })]
                    : []),
                  ...(progress.replies
                    ? [t("wiki.resolve.live.replies", { count: progress.replies })]
                    : []),
                ].join(" · ")}
              </p>
            ) : null}
            <p className="text-xs text-fg-muted">{t("wiki.resolve.nothingSent")}</p>
          </>
        ) : run.data ? (
          <>
            <p className="text-sm font-medium text-fg">
              {t("wiki.resolve.ready", { counts: countsText(run.data.counts) })}
            </p>
            <p className="text-xs text-fg-muted tabular-nums">
              {[
                ...(pagesRevised ? [t("wiki.resolve.live.pages", { count: pagesRevised })] : []),
                t("wiki.resolve.notSent", { count: drafts }),
              ].join(" · ")}
            </p>
            {unanswered ? (
              <p className="text-xs text-fg-secondary">
                {t("wiki.resolve.unanswered", { count: unanswered })}
              </p>
            ) : null}
            {props.canAct ? (
              <div className="flex flex-wrap gap-1.5 pt-1">
                <Button
                  type="button"
                  size="sm"
                  onClick={() =>
                    show({
                      kind: "changes",
                      changeSetId: props.changeSetId,
                      itemId: null,
                      runId: props.runId,
                    })
                  }
                >
                  {t("wiki.resolve.review")}
                </Button>
                {drafts ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="text-fg-secondary"
                    disabled={write.isPending}
                    onClick={() => write.mutate({ kind: "sendAll" })}
                  >
                    <Send aria-hidden="true" strokeWidth={1.75} />
                    {t("wiki.resolve.sendAll")}
                  </Button>
                ) : null}
              </div>
            ) : null}
            {write.isError ? <InlineError>{write.error.message}</InlineError> : null}
          </>
        ) : null}
      </div>
    </section>
  );
}
